import { Draft } from 'immer';
import { CareEnvironment, CarePhase } from '../../../appModel';
import { chooseByCarePhase, CarePhaseDef } from '../../../CarePhaseDef';
import { isFamily } from '../../../isFamily';
import { calcFutureValue } from '../../utils/calcFutureValue';
import { isNullOrUndefined } from '../../../isNullOrUndefined';
import { phaseFamilyCareHours } from '../../carePhases/familyCareShare';

const FULL_CARE_WEEKLY_HOURS = 168;
const WEEKS_PER_MONTH = 52 / 12;

type PhaseCareNeeded = {
  phaseCareHoursNeeded: number;
  phaseCareMonthsNeeded: number;
  isDurationCustom: boolean;
};

function calcPhaseCareNeeded(
  mutableClient: Draft<Client>,
  phaseCareHoursRatio: number,
  phaseDurationRatio: number,
  carePhase: CarePhase,
): PhaseCareNeeded {
  const {
    appliedInferenceSet: { ltcDurationYears, totalCareHoursNeeded },
    carePhaseDurationSelections,
  } = mutableClient;

  if (phaseCareHoursRatio === 0 && phaseDurationRatio === 0) {
    return {
      phaseCareHoursNeeded: 0,
      phaseCareMonthsNeeded: 0,
      isDurationCustom: false,
    };
  }

  const phaseCareMonthsSelected = carePhaseDurationSelections[carePhase];
  const phaseCareMonthsPredicted = Math.max(
    Math.round(ltcDurationYears * phaseDurationRatio * 12),
    1,
  );

  const phaseCareMonthsNeeded = phaseCareMonthsSelected ?? phaseCareMonthsPredicted;

  const phaseCareHoursPredicted = Math.round(totalCareHoursNeeded * phaseCareHoursRatio);
  const phaseCareHoursPerMonthPredicted = phaseCareHoursPredicted / (phaseCareMonthsPredicted ?? 1);
  const phaseCareHoursNeeded = !isNullOrUndefined(phaseCareMonthsSelected)
    ? phaseCareHoursPerMonthPredicted * phaseCareMonthsSelected
    : phaseCareHoursPredicted;

  const isDurationCustom = phaseCareMonthsNeeded !== phaseCareMonthsPredicted;

  return {
    phaseCareHoursNeeded: Math.round(phaseCareHoursNeeded),
    phaseCareMonthsNeeded: Math.round(phaseCareMonthsNeeded),
    isDurationCustom,
  };
}

function calcPhaseCareHoursProvided(supportProviders: SupportProvider[], carePhase: CarePhase) {
  return supportProviders.reduce(
    (hoursProvided, supportProvider) =>
      hoursProvided +
      chooseByCarePhase(
        carePhase,
        supportProvider.supportProviderAppliedPhaseOneHours,
        supportProvider.supportProviderAppliedPhaseTwoHours,
        supportProvider.supportProviderAppliedPhaseThreeHours,
      ),
    0,
  );
}

export function calcPhaseCarePlanDirect(
  mutableClient: Draft<Client>,
  carePhaseDef: CarePhaseDef,
  useClaimProjectionEngine = true,
): PhaseCarePlan {
  const { appliedInferenceSet, supportProviderSet } = mutableClient;

  const carePhase = carePhaseDef.value;
  const appliedCareEnvironment = mutableClient.appliedCareEnvironments[carePhase];
  const recommendedCareEnvironment = mutableClient.recommendedCareEnvironments[carePhase];

  const {
    phaseOneCareHoursRatio,
    phaseTwoCareHoursRatio,
    phaseThreeCareHoursRatio,
    phaseOneDurationYearsRatio,
    phaseTwoDurationYearsRatio,
    phaseThreeDurationYearsRatio,
  } = appliedInferenceSet;

  const { supportProviders } = supportProviderSet;
  const familySupportProviders = supportProviders.filter(isFamily);

  const phaseCareHoursRatio = chooseByCarePhase(
    carePhase,
    phaseOneCareHoursRatio,
    phaseTwoCareHoursRatio,
    phaseThreeCareHoursRatio,
  );

  const phaseDurationRatio = chooseByCarePhase(
    carePhase,
    phaseOneDurationYearsRatio,
    phaseTwoDurationYearsRatio,
    phaseThreeDurationYearsRatio,
  );

  const inHomeEnvironment = appliedCareEnvironment === CareEnvironment.home;

  const { phaseCareHoursNeeded: predictedCareHoursNeeded, phaseCareMonthsNeeded, isDurationCustom } = calcPhaseCareNeeded(
    mutableClient,
    phaseCareHoursRatio,
    phaseDurationRatio,
    carePhase,
  );

  const isFullCareHomeOverride =
    carePhase === CarePhase.fullCare && inHomeEnvironment && mutableClient.clientCustomInferences.fullCare247Override === true;
  const phaseCareHoursNeeded = isFullCareHomeOverride
    ? Math.round(FULL_CARE_WEEKLY_HOURS * WEEKS_PER_MONTH * phaseCareMonthsNeeded)
    : predictedCareHoursNeeded;

  const phaseFamilyCareHoursProvided = phaseFamilyCareHours({
    familyCareHoursProvided: calcPhaseCareHoursProvided(familySupportProviders, carePhase),
    phaseCareHoursNeeded,
    careEnvironment: appliedCareEnvironment,
    familyCareShare: mutableClient.clientCustomInferences.familyCareShare,
    useClaimProjectionEngine,
  });

  const phaseCareHoursProvided = Math.max(phaseFamilyCareHoursProvided, phaseCareHoursNeeded);
  const phaseProfessionalCareHoursProvided = Math.max(
    0,
    phaseCareHoursNeeded - phaseFamilyCareHoursProvided,
  );
  const phaseCareHoursGap = phaseCareHoursNeeded - phaseCareHoursProvided;

  const phaseCareProvidedPercent = phaseCareHoursNeeded
    ? Math.min(1, phaseCareHoursProvided / phaseCareHoursNeeded)
    : 1;

  const phaseFamilyProvidedPercent = phaseCareHoursProvided
    ? Math.min(1, phaseFamilyCareHoursProvided / phaseCareHoursProvided)
    : 0;

  // familyCareHoursRatio calculated later based on all phases
  const familyCareHoursRatio = 0;

  return {
    carePhase,
    appliedCareEnvironment,
    recommendedCareEnvironment,
    inHomeEnvironment,
    isDurationCustom,
    isFullCareHomeOverride,
    phaseCareHoursRatio,
    phaseDurationRatio,
    phaseCareHoursNeeded,
    phaseCareMonthsNeeded,
    phaseFamilyCareHoursProvided,
    phaseProfessionalCareHoursProvided,
    phaseCareHoursGap,
    phaseCareProvidedPercent,
    phaseFamilyProvidedPercent,
    familyCareHoursRatio,
  };
}

export function calcMonthlyCareInflatedValue(
  nominalValue: number,
  inflationRate: number,
  monthIndex: number,
): number {
  const yearsElapsed = monthIndex / 12;
  return calcFutureValue(nominalValue, inflationRate, yearsElapsed);
}
